import {DateTime, Interval} from 'luxon';
import {Pressable, Text} from 'native-base';
import React from 'react';
import {Platform} from 'react-native';
import {MarkingProps} from 'react-native-calendars/src/calendar/day/marking';
import {DateData, DayState} from 'react-native-calendars/src/types';

import useHover from 'hooks/useHover';

interface DayProps {
  date: DateData;
  state: DayState;
  marking: MarkingProps;
  onDayPress: (date: DateData) => void;
  availableStartDate: string;
  availableEndDate: string;
}

const getStyles = ({marking, state, hovering}) => {
  const isSelected = marking?.selected;
  const getTextColor = () => {
    if (marking?.startingDay || marking?.endingDay) {
      return 'white';
    }
    if (isSelected) {
      return 'caesarRewards';
    }
    if (state === 'disabled') {
      return '#DCE0E0';
    }
    return 'tuna';
  };

  const getFontWeight = () => {
    if (marking?.startingDay || marking?.endingDay) {
      return 700;
    }
    if (isSelected || state === 'today') {
      return 600;
    }
    return 500;
  };

  const getBackgroundColor = () => {
    if (marking?.startingDay || marking?.endingDay) {
      return 'caesarRewards';
    }
    if (isSelected || hovering) {
      return 'ghostWhite';
    }
    return 'white';
  };

  return {
    textColor: getTextColor(),
    fontWeight: getFontWeight(),
    backgroundColor: getBackgroundColor(),
  };
};

const isDisabled = ({availableEndDate, availableStartDate, date}) => {
  const now = DateTime.now().startOf('day');
  let start = DateTime.fromISO(availableStartDate).startOf('day');
  if (start < now) {
    start = now;
  }
  const interval = Interval.fromDateTimes(
    start,
    DateTime.fromISO(availableEndDate).plus({days: 1}).startOf('day')
  );
  return !interval.contains(DateTime.fromISO(date));
};

const Day: React.FC<DayProps> = ({
  date,
  state,
  marking,
  onDayPress,
  availableEndDate,
  availableStartDate,
}) => {
  const {hovering, handleHoverIn, handleHoverOut} = useHover();
  const {textColor, backgroundColor, fontWeight} = getStyles({
    state,
    marking,
    hovering,
  });
  const isDayDisabled = isDisabled({
    availableStartDate,
    availableEndDate,
    date: date.dateString,
  });
  const isSelected = marking?.selected;
  return (
    <Pressable
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      cursor={
        Platform.OS === 'web'
          ? isDayDisabled
            ? 'not-allowed'
            : 'pointer'
          : null
      }
      disabled={!isSelected && isDayDisabled}
      height={50}
      width="100%"
      alignItems="center"
      justifyContent="center"
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onPress={() => onDayPress(date)}
      backgroundColor={backgroundColor}
      borderRightRadius={marking?.endingDay ? 5 : 0}
      borderLeftRadius={marking?.startingDay ? 5 : 0}>
      <Text
        fontWeight={fontWeight}
        textAlign="center"
        fontSize="13px"
        color={textColor}>
        {date.day}
      </Text>
    </Pressable>
  );
};

export default Day;
